import { useSwipeable } from 'react-swipeable';
import ImageZoom from '../ImageZoom/ImageZoom';
import ImagePicker from '../inputFields/ImagePicker';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import React from 'react';

function ProductImages({
    productData,
    isTab,
    editable = false,
    images = [],
    videos = [],
    onReplaceImage,
    onAddImage,
    onRemoveImage,
    onReplaceVideo,
    onAddVideo,
    onRemoveVideo,
}) {
    const [selectedMedia, setSelectedMedia] = useState({ url: "", type: "image" });
    const thumbnailRef = useRef(null);

    // Combine images and videos into a single media array for navigation
    const allMedia = useMemo(() => {
        const media = [];
        if (editable) {
            images.forEach(img => {
                media.push({ url: img.preview || img.url || img.image, type: 'image', id: img.id });
            });
            videos.forEach(vid => {
                media.push({ url: vid.preview || vid.url || vid.video, type: 'video', id: vid.id });
            });
        } else {
            (productData?.product_images || []).forEach(img => {
                media.push({ url: img.image, type: 'image', id: img.id });
            });
            (productData?.product_videos || []).forEach(vid => {
                const videoUrl = typeof vid === 'string' ? vid : vid.video || vid.url;
                media.push({ url: videoUrl, type: 'video', id: vid.id });
            });
        }
        return media;
    }, [editable, images, videos, productData]);

    const handleSwipeNext = useCallback(() => {
        setSelectedMedia((prevMedia) => {
            const currentIndex = allMedia.findIndex(item => item.url === prevMedia.url && item.type === prevMedia.type);
            const nextIndex = (currentIndex + 1) % allMedia.length;
            return allMedia[nextIndex] || prevMedia;
        });
    }, [allMedia]);

    const handleSwipePrev = useCallback(() => {
        setSelectedMedia((prevMedia) => {
            const currentIndex = allMedia.findIndex(item => item.url === prevMedia.url && item.type === prevMedia.type);
            const prevIndex = (currentIndex - 1 + allMedia.length) % allMedia.length;
            return allMedia[prevIndex] || prevMedia;
        });
    }, [allMedia]);

    const swipeHandlers = useSwipeable({
        onSwipedLeft: handleSwipeNext,
        onSwipedRight: handleSwipePrev,
        preventScrollOnSwipe: true,
        trackMouse: true // enables mouse dragging as well
    });

    const thumbnails = useMemo(() => {
        return allMedia.map((item, index) => {
            const isSelected = selectedMedia.url === item.url && selectedMedia.type === item.type;
            return (
                <div
                    key={`${item.type}-${item.id || index}`}
                    className={`cursor-pointer inline-block h-20 w-20 items-center justify-center text-center aspect-square relative ${isSelected ? 'border-2 border-primary rounded-lg' : ''}`}
                    onClick={() => setSelectedMedia({ url: item.url, type: item.type })}
                >
                    {item.type === 'video' ? (
                        <>
                            <video
                                src={item.url}
                                className="rounded-lg object-contain max-h-20 aspect-square"
                                muted
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                </svg>
                            </div>
                        </>
                    ) : (
                        <img
                            src={item.url}
                            className="rounded-lg object-contain max-h-20 aspect-square"
                            alt={`Thumbnail ${index + 1}`}
                        />
                    )}
                </div>
            );
        });
    }, [allMedia, selectedMedia]);

    useEffect(() => {
        if (allMedia.length > 0 && (!selectedMedia.url || !allMedia.find(item => item.url === selectedMedia.url && item.type === selectedMedia.type))) {
            setSelectedMedia({ url: allMedia[0].url, type: allMedia[0].type });
        }
    }, [allMedia, editable, productData, selectedMedia]);

    // Scroll the thumbnails left or right by a fixed amount
    const scrollThumbnails = (direction) => {
        if (thumbnailRef.current) {
            const scrollAmount = 100; // Adjust scroll amount as needed
            thumbnailRef.current.scrollBy({
                left: direction === "right" ? scrollAmount : -scrollAmount,
                behavior: "smooth"
            });
        }
    };

    if (editable) {
        return (
            <div className="flex flex-col">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {images.map((img, index) => (
                        <div key={img.id || index} className="relative w-full h-44">
                            <ImagePicker
                                preloadedImage={img.preview || img.url || img.image}
                                placeholder={"Upload Image"}
                                onImageSelect={(file) => onReplaceImage && onReplaceImage(index, file)}
                                needCoverImage={false}
                            />
                            <div className="absolute top-2 right-2 flex gap-2">
                                {onRemoveImage && (
                                    <button type="button" onClick={() => onRemoveImage(img)} className="bg-red-500 text-white rounded px-2 py-1 text-xs">Remove</button>
                                )}
                            </div>
                        </div>
                    ))}
                    {videos.map((vid, index) => (
                        <div key={vid.id || index} className="relative w-full h-44">
                            <div className="w-full h-full border rounded-lg overflow-hidden bg-gray-100">
                                {vid.preview || vid.url || vid.video ? (
                                    <video
                                        src={vid.preview || vid.url || vid.video}
                                        className="w-full h-full object-contain"
                                        controls={false}
                                        muted
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                        No video preview
                                    </div>
                                )}
                            </div>
                            <div className="absolute top-2 right-2 flex gap-2">
                                {onReplaceVideo && (
                                    <label className="bg-blue-500 text-white rounded px-2 py-1 text-xs cursor-pointer">
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="video/*"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file && onReplaceVideo) onReplaceVideo(index, file);
                                                e.currentTarget.value = "";
                                            }}
                                        />
                                        Replace
                                    </label>
                                )}
                                {onRemoveVideo && (
                                    <button type="button" onClick={() => onRemoveVideo(vid)} className="bg-red-500 text-white rounded px-2 py-1 text-xs">Remove</button>
                                )}
                            </div>
                        </div>
                    ))}
                    {onAddImage && (
                        <div className="w-full h-44 border-2 border-dashed flex items-center justify-center text-gray-500 hover:bg-gray-100">
                            <label className="w-full h-full flex items-center justify-center cursor-pointer">
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) onAddImage(file);
                                    e.currentTarget.value = "";
                                }} />
                                + Add Image
                            </label>
                        </div>
                    )}
                    {onAddVideo && (
                        <div className="w-full h-44 border-2 border-dashed flex items-center justify-center text-gray-500 hover:bg-gray-100">
                            <label className="w-full h-full flex items-center justify-center cursor-pointer">
                                <input type="file" className="hidden" accept="video/*" onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) onAddVideo(file);
                                    e.currentTarget.value = "";
                                }} />
                                + Add Video
                            </label>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="relative flex flex-col">
            <div className="flex flex-row justify-center">
                <div className="flex justify-center mb-6 items-center aspect-square object-contain max-w-96">
                    {selectedMedia.type === 'video' ? (
                        <div {...swipeHandlers} className="w-full h-80 object-contain">
                            <video
                                src={selectedMedia.url}
                                className="w-full h-80 object-contain"
                                controls
                                autoPlay
                            />
                        </div>
                    ) : isTab ? (
                        <div {...swipeHandlers} className="w-full h-80 object-contain">
                            <img
                                src={selectedMedia.url}
                                className="w-full h-80 object-contain"
                                alt="Selected Product"
                            />
                        </div>
                    ) : (
                        <div className="h-80 object-contain">
                            <ImageZoom
                                src={selectedMedia.url}
                                className="w-full h-80 object-contain"
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="relative flex items-center">
                {!isTab && (
                    <div
                        onClick={() => scrollThumbnails("left")}
                        className="absolute left-0 z-10 p-2 bg-black bg-opacity-5 rounded-full cursor-pointer"
                        style={{ transform: "translateX(-50%)" }}
                    >
                        &lt;
                    </div>
                )}

                <div
                    ref={thumbnailRef}
                    className="flex overflow-x-auto whitespace-nowrap space-x-3 mb-6 h-22 px-2"
                >
                    {thumbnails}
                </div>

                {!isTab && (
                    <div
                        onClick={() => scrollThumbnails("right")}
                        className="absolute right-0 z-10 p-2 bg-black bg-opacity-5 rounded-full cursor-pointer"
                        style={{ transform: "translateX(50%)" }}
                    >
                        &gt;
                    </div>
                )}
            </div>
        </div>
    );
}

export default React.memo(ProductImages);
